import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import { clearWorkoutItems } from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ModalTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const ModalDescription = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 20px;
`;

const StyledLink = styled('span')`
  color: #007FAE;
  text-decoration: underline;
  cursor: pointer;
`;

const DateContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const DateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
`;

const DatePickerContainer = styled('div')`
  width: 48%;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 20px;
  cursor: pointer;
`;

const StyledCheckbox = styled('input')`
  margin-right: 10px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const CheckboxLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  cursor: pointer;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const ClearButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: #FF0505;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #D32F2F;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const formatDateString = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

const ClearWorkoutItemsModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  // Use modalData.date if provided (from week view), otherwise use selectedDate from Redux
  const initialDate = modalData?.date ? new Date(modalData.date) : new Date(selectedDate);

  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);
  const [includeParentCalendars, setIncludeParentCalendars] = useState(false);

  const handleClearItems = () => {
    const formattedStartDate = formatDateString(startDate);
    const formattedEndDate = formatDateString(endDate);
    const submissionObject = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      clearParents: includeParentCalendars,
      clearWorkoutInfo: false, // By default, we're not clearing workout info
    };
    dispatch(clearWorkoutItems(
      currentUser.accountCode,
      submissionObject,
    ));
    closeModal();
  };

  const handleLearnMoreClick = () => {
    window.open('https://support.teambuildr.com', '_blank');
  };

  return (
    <ModalContainer>
      <ModalTitle>Clear Workout Items</ModalTitle>
      <ModalDescription>
        Recurring workout items cannot be cleared using this method.
        {' '}
        <StyledLink onClick={handleLearnMoreClick}>Click here to learn more.</StyledLink>
      </ModalDescription>

      <DateContainer>
        <DatePickerContainer>
          <DateLabel>Start Date</DateLabel>
          <TBDatePicker
            activeDate={formatDateString(startDate)}
            setActiveDate={setStartDate}
            additionalStyles={{ margin: '8px', maxWidth: '100%' }}
            showDropShadow={false}
            showIcon={false}
            maxDate={null}
          />
        </DatePickerContainer>

        <DatePickerContainer>
          <DateLabel>End Date</DateLabel>
          <TBDatePicker
            activeDate={formatDateString(endDate)}
            setActiveDate={setEndDate}
            additionalStyles={{ margin: '8px', maxWidth: '100%' }}
            showDropShadow={false}
            showIcon={false}
            maxDate={null}
            minDate={startDate}
          />
        </DatePickerContainer>
      </DateContainer>

      <CheckboxContainer onClick={() => setIncludeParentCalendars(!includeParentCalendars)}>
        <StyledCheckbox
          type='checkbox'
          checked={includeParentCalendars}
          onChange={() => setIncludeParentCalendars(!includeParentCalendars)}
        />
        <CheckboxLabel>Clear items originating from parent calendars</CheckboxLabel>
      </CheckboxContainer>

      <ButtonsContainer>
        <ClearButton onClick={handleClearItems}>CLEAR WORKOUT ITEMS</ClearButton>
        <CancelButton onClick={closeModal}>CANCEL</CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ClearWorkoutItemsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFromWeekView: PropTypes.bool,
  }),
};

ClearWorkoutItemsModal.defaultProps = {
  modalData: null,
};

export default ClearWorkoutItemsModal;
